import { type BackupVersion, backupHistoryCovers, selectBackupRetention } from "@classroom/domain";
import {
  BACKUP_RETENTION_POLICY,
  type BackupCatalogEntry,
  type BackupCipher,
  type BackupConfiguration,
} from "../../../../../supabase/functions/_shared/backupTypes";
import { digest } from "../../../../../supabase/functions/sync-control/policy";
import { openAutomaticBackup, sealAutomaticBackup } from "../localStore/automaticBackupCrypto";
import { assertBackupSource } from "../localStore/encryptedBackup";
import type { AutomaticBackupSource, LocalBackupSource, LocalSnapshot } from "../localStore/repository";
import type { BackupDrive } from "./backupDrive";
import type { BackupJob } from "./backupVault";
import { canonical } from "./cryptoV2";

export type BackupCatalog = {
  config: BackupConfiguration;
  entries: BackupCatalogEntry[];
  returnPoint?: string;
  garbage: string[];
  recoveryVerified: boolean;
};
export type BackupRecord = { version: BackupVersion; manifest: BackupCipher };
export type BackupLocal = {
  capture(): Promise<AutomaticBackupSource>;
  validate(source: LocalBackupSource): Promise<void>;
  acknowledge(revision: number | null, time: number, restoreId?: string): Promise<void>;
  restore(snapshot: LocalSnapshot, expectedHash: string): Promise<void>;
};
export type BackupControl = { request<T>(action: string, payload?: Record<string, unknown>): Promise<T> };
export type BackupTasks = { read(): Promise<BackupJob | undefined>; write(job?: BackupJob): Promise<void> };
type FullBackup = { type: "full"; version: BackupVersion; source: LocalBackupSource };

function assertVersion(value: BackupVersion, workspaceId: string, id: string) {
  if (
    !value ||
    value.id !== id ||
    value.workspaceId !== workspaceId ||
    value.kind !== "automatic" ||
    typeof value.deviceId !== "string" ||
    (value.datasetId !== null && typeof value.datasetId !== "string") ||
    (value.generation !== null && typeof value.generation !== "string") ||
    (value.datasetId === null) !== (value.generation === null) ||
    (value.restoreId !== undefined && (typeof value.restoreId !== "string" || value.restoreId.length > 128)) ||
    !Number.isSafeInteger(value.revision) ||
    value.revision < 0 ||
    typeof value.contentHash !== "string" ||
    !/^[\w-]{43}$/.test(value.contentHash) ||
    !Number.isSafeInteger(value.dataAt) ||
    !Number.isSafeInteger(value.completedAt) ||
    !value.clock ||
    typeof value.clock !== "object" ||
    Array.isArray(value.clock) ||
    Object.values(value.clock).some((n) => !Number.isSafeInteger(n) || n < 0)
  )
    throw new Error("sync-backup-format");
}
export class AutomaticBackup {
  private leaseId = "";
  private lost = false;
  private active = false;
  constructor(
    private readonly options: {
      workspaceId: string;
      deviceId: string;
      key: CryptoKey;
      control: BackupControl;
      drive: BackupDrive;
      local: BackupLocal;
      tasks: BackupTasks;
      now?: () => number;
      assertRestore?: () => void;
      afterRestore?: () => Promise<void>;
      abort?: () => void;
      crypto?: { seal: typeof sealAutomaticBackup; open: typeof openAutomaticBackup; digest: typeof digest };
    },
  ) {}
  private get crypto() {
    return this.options.crypto ?? { seal: sealAutomaticBackup, open: openAutomaticBackup, digest };
  }
  private now() {
    return (this.options.now ?? Date.now)();
  }
  private assertLease() {
    if (this.lost) throw new Error("sync-backup-lease-lost");
  }
  async catalog(): Promise<{ records: BackupRecord[]; catalog: BackupCatalog }> {
    const catalog = await this.options.control.request<BackupCatalog>("backup-list");
    if (
      catalog.config.workspaceId !== this.options.workspaceId ||
      !Array.isArray(catalog.entries) ||
      catalog.entries.length > 128
    )
      throw new Error("sync-backup-format");
    const records: BackupRecord[] = [];
    for (const entry of catalog.entries) {
      const value = (await this.crypto.open(this.options.key, this.options.workspaceId, entry.id, entry.manifest)) as {
        type: string;
        version: BackupVersion;
      };
      if (value.type !== "manifest") throw new Error("sync-backup-format");
      assertVersion(value.version, this.options.workspaceId, entry.id);
      records.push({
        version: { ...value.version, completedAt: entry.verifiedAt, protected: entry.id === catalog.returnPoint },
        manifest: entry.manifest,
      });
    }
    records.sort((a, b) => b.version.completedAt - a.version.completedAt);
    return { records, catalog };
  }
  private async decode(id: string, cipher: unknown): Promise<FullBackup> {
    const value = (await this.crypto.open(
      this.options.key,
      this.options.workspaceId,
      id,
      cipher as BackupCipher,
    )) as FullBackup;
    if (value?.type !== "full") throw new Error("sync-backup-format");
    assertVersion(value.version, this.options.workspaceId, id);
    assertBackupSource(value.source);
    if (
      value.source.snapshot.workspace.id !== this.options.workspaceId ||
      (await this.crypto.digest(value.source.snapshot)) !== value.version.contentHash
    )
      throw new Error("sync-backup-format");
    await this.options.local.validate(value.source);
    return value;
  }
  async inspect(id: string): Promise<FullBackup> {
    const { records } = await this.catalog();
    const selected = records.find((record) => record.version.id === id);
    if (!selected) throw new Error("sync-backup-not-found");
    const value = await this.decode(id, await this.options.drive.read(id));
    if (
      value.version.datasetId !== selected.version.datasetId ||
      value.version.generation !== selected.version.generation ||
      value.version.restoreId !== selected.version.restoreId ||
      value.version.dataAt !== selected.version.dataAt ||
      value.version.contentHash !== selected.version.contentHash ||
      value.version.deviceId !== selected.version.deviceId ||
      value.version.revision !== selected.version.revision ||
      canonical(value.version.clock) !== canonical(selected.version.clock)
    )
      throw new Error("sync-backup-format");
    return value;
  }
  private async locked<T>(action: () => Promise<T>): Promise<T> {
    if (this.active) throw new Error("sync-backup-busy");
    this.active = true;
    this.lost = false;
    this.leaseId = crypto.randomUUID();
    let timer: ReturnType<typeof setInterval> | undefined;
    try {
      await this.options.control.request("backup-lease", { jobId: this.leaseId });
      timer = setInterval(() => {
        void this.options.control.request("backup-renew", { leaseId: this.leaseId }).catch(() => {
          this.lost = true;
          this.options.abort?.();
        });
      }, 30000);
      return await action();
    } finally {
      if (timer) clearInterval(timer);
      await this.options.control.request("backup-release", { leaseId: this.leaseId }).catch(() => undefined);
      this.active = false;
    }
  }
  private async complete(job: BackupJob, safeguard: boolean) {
    this.assertLease();
    let uploaded = await this.options.drive.read(job.fileId);
    if (!uploaded) {
      try {
        await this.options.drive.create(job.fileId, job.cipher, "automatic");
      } catch (error) {
        if (!(error instanceof Error) || error.message !== "sync-drive-exists") throw error;
      }
      uploaded = await this.options.drive.read(job.fileId);
    }
    // Unknown upload outcomes reuse their allocated ID and exact ciphertext.
    if (canonical(uploaded) !== canonical(job.cipher)) throw new Error("sync-backup-format");
    const checked = await this.decode(job.fileId, uploaded);
    if (canonical(checked.version) !== canonical(job.version)) throw new Error("sync-backup-format");
    this.assertLease();
    const published = await this.options.control.request<{ verifiedAt: number }>("backup-publish", {
      leaseId: this.leaseId,
      id: job.fileId,
      manifest: job.manifest,
      safeguard,
    });
    await this.options.local.acknowledge(job.version.revision, published.verifiedAt, job.version.restoreId);
    await this.options.local.acknowledge(null, this.now());
    await this.options.tasks.write(undefined);
    return checked;
  }
  private async save(safeguard: boolean): Promise<FullBackup> {
    const previous = await this.options.tasks.read();
    // Finish an interrupted upload before capturing again, but never mistake
    // that old snapshot for the current restore safeguard.
    if (previous) await this.complete(previous, false);
    const captured = await this.options.local.capture();
    assertBackupSource(captured.source);
    const hash = await this.crypto.digest(captured.source.snapshot);
    const { records, catalog } = await this.catalog();
    if (!catalog.recoveryVerified) throw new Error("sync-backup-verification-required");
    const version: BackupVersion = {
      id: "pending",
      workspaceId: this.options.workspaceId,
      deviceId: this.options.deviceId,
      revision: captured.progress.revision,
      datasetId: captured.sync?.datasetId ?? null,
      generation: captured.sync?.generation ?? null,
      clock: captured.sync?.clock ?? {},
      contentHash: hash,
      dataAt: captured.progress.lastChangeAt ?? this.now(),
      completedAt: this.now(),
      kind: "automatic",
      ...(captured.progress.lastRestoreId ? { restoreId: captured.progress.lastRestoreId } : {}),
    };
    if (
      captured.sync &&
      records.some(({ version: other }) => other.contentHash !== hash && backupHistoryCovers(other, version))
    )
      throw new Error("sync-backup-sync-required");
    const restorePending =
      captured.progress.lastRestoreId && captured.progress.lastRestoreId !== captured.progress.lastBackedUpRestoreId;
    // A deliberate restore needs its own causal version, even if the data match
    // an older save. Otherwise retention could discard the current state as old.
    const duplicate = records.find(
      (record) =>
        record.version.contentHash === hash &&
        !records.some(
          ({ version: other }) => other.contentHash !== hash && backupHistoryCovers(other, record.version),
        ) &&
        (!restorePending || record.version.restoreId === captured.progress.lastRestoreId),
    );
    if (duplicate) {
      const checked = await this.inspect(duplicate.version.id);
      this.assertLease();
      await this.options.control.request("backup-publish", {
        leaseId: this.leaseId,
        id: duplicate.version.id,
        manifest: duplicate.manifest,
        safeguard,
      });
      await this.options.local.acknowledge(
        captured.progress.revision,
        duplicate.version.completedAt,
        captured.progress.lastRestoreId,
      );
      await this.options.local.acknowledge(null, this.now());
      return checked;
    }
    const id = await this.options.drive.allocateId();
    version.id = id;
    const full: FullBackup = { type: "full", version, source: captured.source };
    const job: BackupJob = {
      id: crypto.randomUUID(),
      fileId: id,
      version,
      cipher: await this.crypto.seal(this.options.key, this.options.workspaceId, id, full),
      manifest: await this.crypto.seal(this.options.key, this.options.workspaceId, id, {
        type: "manifest",
        version,
      }),
    };
    await this.options.tasks.write(job);
    return this.complete(job, safeguard);
  }
  private async clean() {
    this.assertLease();
    const { records, catalog } = await this.catalog();
    if (!catalog.config.enabled) return;
    const policy = selectBackupRetention(
      records.map((r) => r.version),
      this.now(),
    );
    const result = policy.remove.length
      ? await this.options.control.request<{ garbage: string[] }>("backup-prune", {
          leaseId: this.leaseId,
          ids: policy.remove,
          retentionPolicy: BACKUP_RETENTION_POLICY,
        })
      : { garbage: catalog.garbage };
    for (const id of result.garbage) {
      this.assertLease();
      await this.options.drive.remove(id);
      await this.options.control.request("backup-delete-ack", { leaseId: this.leaseId, ids: [id] });
    }
  }
  run() {
    return this.locked(async () => {
      const result = await this.save(false);
      await this.clean();
      return result;
    });
  }
  restore(id: string) {
    return this.locked(async () => {
      this.options.assertRestore?.();
      const selected = await this.inspect(id);
      const current = await this.save(true);
      this.assertLease();
      this.options.assertRestore?.();
      // The database Worker checks this fingerprint under its write lock.
      // A save/download during the safeguard upload makes restore fail safely.
      await this.options.local.restore(selected.source.snapshot, current.version.contentHash);
      await this.options.afterRestore?.();
      await this.save(false);
      await this.clean();
    });
  }
}
